import { useMemo, useCallback } from "react";

/**
 * Hook personnalisé pour gérer les URLs des photos
 * @returns {Object} Méthodes pour manipuler les URLs de photos
 */
export const usePhotoUrl = () => {
  // Base URL cohérente avec axiosConfig (relatif "/api" en prod)
  const apiBaseUrl = process.env.REACT_APP_API_URL || "/api";
  /**
   * Construit l'URL complète pour une photo basée sur le chemin stocké
   * @param {string} photoPath Le chemin de la photo tel que stocké dans la base de données
   * @param {string} defaultImagePath Image par défaut à utiliser si photoPath est vide (optionnel)
   * @returns {string} L'URL complète de l'image
   */
  const getPhotoUrl = useCallback(
    (photoPath, defaultImagePath = "assets/icons/logo.svg") => {
      if (!photoPath) {
        return defaultImagePath; // Image par défaut
      }

      // Si le chemin commence déjà par http, c'est une URL complète
      if (photoPath.startsWith("http")) {
        return photoPath;
      }

      // Le backend sert les fichiers via GET /api/file?path=<bucket>/<chemin>
      return `${apiBaseUrl}/file?path=${encodeURIComponent(photoPath)}`;
    },
    [apiBaseUrl]
  );

  /**
   * Détermine si une URL d'image est une URL absolue (http/https)
   * @param {string} url L'URL à vérifier
   * @returns {boolean} indiquant si c'est une URL absolue
   */
  const isAbsoluteUrl = (url) => {
    return url?.startsWith("http://") || url?.startsWith("https://");
  };

  /**
   * Retourne l'URL de base de l'API
   * @returns {string} L'URL de base de l'API
   */
  const getApiBaseUrl = useCallback(() => {
    return apiBaseUrl;
  }, [apiBaseUrl]);

  // Utilisation de useMemo pour éviter de recréer l'objet à chaque rendu
  return useMemo(
    () => ({
      getPhotoUrl,
      isAbsoluteUrl,
      getApiBaseUrl,
    }),
    [getApiBaseUrl, getPhotoUrl]
  );
};
